import React, { useMemo, useRef } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  hideLinkbar,
  toggleLinksSidebar,
  toggleShareModalAction,
} from "../../store/actions";
import { useHistory } from "react-router";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { trackAdobeAppClick, trackAdobeClick } from "../../utils/adobeLogging";
import { AuthCommandFacade } from "../auth/AuthCommandFacade";

const LinksSidebar = () => {
  const isSidebarVisible = useSelector((state) => state.ui.linksSidebar);
  const dispatch = useDispatch();
  const history = useHistory();
  const section = useSelector((state) => state.progress.section);
  const lang = useSelector((state) => state.ui.lang);
  const currentUser = useSelector((state) => state.user);
  const pages = useSelector((state) =>
    _.sortBy(
      Object.values(state.pages).filter(
        (i) => i.pmApproach.includes(state.progress.section) && !i.data.hidden
      ),
      [(page) => page.data.order]
    )
  );
  const { t } = useTranslation();

  const sidebarRef = useRef(null);

  useOutsideClick(sidebarRef, () => {
    if (isSidebarVisible) {
      dispatch(hideLinkbar());
    }
  });

  const sidebarStyles = useMemo(() => {
    return {
      width: "375px",
      right: 0,
      top: 0,
      position: "absolute",
      height: "fit-content",
    };
  }, []);

  const onLinkClick = (path, name) => {
    if (isSidebarVisible) {
      dispatch(toggleLinksSidebar());
    }

    trackAdobeClick(null, {
      linkTitle: name,
      linkModule: `${section}-links-sidebar`,
      targetURL: path,
    });

    history.push(path);
  };

  const onShareClick = (path,name) => {
    if (isSidebarVisible) {
      dispatch(toggleLinksSidebar());
    }
    trackAdobeAppClick({action: "share",language: lang})
    trackAdobeClick(null, {
      linkTitle: name,
      linkModule: `${section}-links-sidebar`,
      targetURL: path,
    });
    
    dispatch(toggleShareModalAction());
  };

  const onLogout = () => {
    if (isSidebarVisible) {
      dispatch(toggleLinksSidebar());
    }

    trackAdobeClick(null, {
      linkTitle: t("logout"),
      linkModule: `${section}-links-sidebar`,
      targetURL: "/logout",
    });

    const auth = new AuthCommandFacade(dispatch, history, lang, currentUser);
    auth.logout();
  };

  const onFeedback = () => {
    if (isSidebarVisible) {
      dispatch(toggleLinksSidebar());
    }

    trackAdobeClick(null, {
      linkTitle: t("feedbackSurvey"),
      linkModule: `${section}-links-sidebar`,
      targetURL: "https://www.surveymonkey.com/r/R6CJJC9",
    });

    window.open("https://www.surveymonkey.com/r/R6CJJC9", "_blank");
  };

  const itemClass =
    "uppercase font-bold cursor-pointer py-4 px-3 text-gray-800 hover:text-white hover-bg-blue-menu";

  return (
    <div>
      {isSidebarVisible && (
        <>
          <div className="fixed inset-0 transition-opacity">
            <div className="absolute inset-0 bg-gray-500 opacity-75" />
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" />
          &#8203;
        </>
      )}
      <AnimatePresence>
        {isSidebarVisible && (
          <motion.div
            initial={{ x: "200%" }}
            animate={{ x: 0 }}
            exit={{ x: "200%" }}
            transition={{ damping: 200 }}
            className="bg-white absolute shadow-lg"
            style={sidebarStyles}
            ref={sidebarRef}
          >
            <div
              adoberegion={`${section}-links-sidebar`}
              className="flex flex-col justify-between bg-blue-menu"
              style={{ minHeight: "50px" }}
            >
              <div className="text-right">
                <button
                  className="m-5"
                  onClick={() => dispatch(toggleLinksSidebar())}
                >
                  <i className="fas fa-chevron-circle-right" />
                </button>
              </div>
              <div className="block uppercase font-bold text-md p-5 text-white">
                {t("additionalResources")}
              </div>
            </div>
            <ul className="nav list-none tracking-widest h-full lg:h-auto overflow-y-auto flex-col flex items-center lg:block lg:overflow-hidden">
              <li onClick={() => onShareClick("/app/share",t("share"))} className={itemClass}>
                {t("share")}
              </li>
              <li
                onClick={() =>
                  onLinkClick("/app/earn-badges", t("earnBadgesPageTitle"))
                }
                className={itemClass}
              >
                {t("earnBadgesPageTitle")}
              </li>

              <li
                onClick={() => onLinkClick("/app/key-terms", t("keyTerms"))}
                className={itemClass}
              >
                {t("keyTerms")}
              </li>
              <li
                onClick={() =>
                  onLinkClick(
                    "/app/tools-and-templates",
                    t("toolsAndTemplates")
                  )
                }
                className={itemClass}
              >
                {t("toolsAndTemplates")}
              </li>
              <li onClick={() => onFeedback()} className={itemClass}>
                {t("feedbackSurvey")}
              </li>
              {pages.map((page) => (
                <li
                  key={page.contentKey}
                  onClick={() =>
                    onLinkClick(
                      `/app/pages/${page.contentKey}`,
                      page.contentKey
                    )
                  }
                  className={itemClass}
                >
                  <span className="uppercase">
                    {page.data && page.data.title
                      ? page.data.title
                      : page.title}
                  </span>
                </li>
              ))}
              <li
                onClick={() => onLogout()}
                className={itemClass}
              >
                {t("logout")}
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LinksSidebar;
